import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
} from '@mui/material';
import CpuIcon from '@mui/icons-material/Memory';
import StorageIcon from '@mui/icons-material/Storage';
import DisplayIcon from '@mui/icons-material/DesktopWindows';
import { useLanguage } from '../contexts/LanguageContext';
import { useContent } from '../contexts/ContentContext';
import SchemaComponent from '../components/SchemaComponent';
import ImageCarousel from '../components/ImageCarousel';
import { sendLaptopDetailsViaWhatsApp } from '../utils/whatsappSender';
import './PremiumLaptops.css';

const CONDITIONS = { all: 'all', new: 'new', refurbished: 'refurbished', used: 'used' };

const PremiumLaptops = () => {
  const { language } = useLanguage();
  const { content } = useContent();
  const [laptops, setLaptops] = useState([]);
  const [filter, setFilter] = useState(CONDITIONS.all);
  const [laptopSchema, setLaptopSchema] = useState(null);
  const [carouselLaptop, setCarouselLaptop] = useState(null);
  const [carouselOpen, setCarouselOpen] = useState(false);

  // Map URL patterns to conditions
  const urlToCondition = {
    'new-laptops': 'new',
    'refurbished-laptops': 'refurbished',
    'used-laptops': 'used',
    'all-laptops': 'all',
    'premium-laptops': 'all',
  };

  const conditionToUrl = {
    all: 'all-laptops',
    new: 'new-laptops',
    refurbished: 'refurbished-laptops',
    used: 'used-laptops',
  };

  // Initialize filter from URL hash on mount
  useEffect(() => {
    const hash = window.location.hash.substring(2); // Remove #/
    const condition = urlToCondition[hash];
    if (condition) {
      setFilter(condition);
    }
    // Scroll to Premium Laptops section on mount if hash is set
    if (hash && Object.keys(urlToCondition).includes(hash)) {
      setTimeout(() => {
        const element = document.getElementById('premium-laptops-section');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/laptops`)
      .then((res) => res.json())
      .then((data) => setLaptops(Array.isArray(data) ? data : []))
      .catch(() => setLaptops([]));
  }, []);

  // Generate Product Schema for Laptops
  useEffect(() => {
    if (laptops && laptops.length > 0) {
      const productSchemas = laptops.map(laptop => ({
        "@context": "https://schema.org",
        "@type": "Product",
        "name": laptop.name || "Premium Laptop",
        "description": `${laptop.brand} ${laptop.name} - ${laptop.cpu} | ${laptop.ram}`,
        "image": laptop.image || "https://alrasheedcomputers.com/default-laptop.jpg",
        "brand": {
          "@type": "Brand",
          "name": laptop.brand || "Al-Rasheed"
        },
        "offers": {
          "@type": "AggregateOffer",
          "priceCurrency": "SAR",
          "price": (laptop.price || "").replace(/[^\d.]/g, '') || "0",
          "availability": "https://schema.org/InStock",
          "condition": laptop.condition === 'used' ? 'https://schema.org/UsedCondition' : 
                      laptop.condition === 'refurbished' ? 'https://schema.org/RefurbishedCondition' : 
                      'https://schema.org/NewCondition'
        },
        "sku": laptop.id ? `laptop-${laptop.id}` : undefined,
        "url": "https://alrasheedcomputers.com/#premium-laptops"
      }));

      const collectionSchema = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Premium Laptops",
        "description": "Quality pre-owned and new laptops at unbeatable prices",
        "url": "https://alrasheedcomputers.com/#premium-laptops",
        "mainEntity": productSchemas
      };

      setLaptopSchema(collectionSchema);
    }
  }, [laptops]);

  // Update URL when filter changes
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    const newUrl = conditionToUrl[newFilter] || 'all-laptops';
    window.location.hash = `/${newUrl}`;
  };

  const t = content[language] || content.ar;
  const filterLabels =
    language === 'ar'
      ? { all: 'الكل', new: 'جديد', refurbished: 'معاد تجديده', used: 'مستعمل' }
      : { all: 'All', new: 'New', refurbished: 'Refurbished', used: 'Used' };

  const filtered =
    filter === CONDITIONS.all
      ? laptops
      : laptops.filter((item) => (item.condition || '').toLowerCase() === filter);

  return (
    <Box sx={{ minHeight: 'calc(100vh - 200px)' }} id="premium-laptops-section">
      {/* Inject Product Schema for Search Engines */}
      <SchemaComponent schema={laptopSchema} id="laptop-products-schema" />
      <ImageCarousel
        images={carouselLaptop?.image ? [carouselLaptop.image] : []}
        open={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        laptopName={carouselLaptop?.name}
      />
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8, textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Typography variant="h1" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
            {t.premiumLaptopsTitle}
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9 }}>
            {t.premiumLaptopsSubtitle}
          </Typography>
        </Container>
      </Box>

      <Box sx={{ py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mb: 4 }}>
            {Object.entries(CONDITIONS).map(([key, value]) => (
              <Button
                key={key}
                variant={filter === value ? 'contained' : 'outlined'}
                onClick={() => handleFilterChange(value)}
                sx={{ textTransform: 'none', borderRadius: 10 }}
              >
                {filterLabels[key]}
              </Button>
            ))}
          </Box>

          <Grid container spacing={4}>
            {filtered.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={item.id || index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    transition: 'box-shadow 0.3s, transform 0.3s',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Box sx={{ position: 'relative', cursor: 'pointer' }} onClick={() => { setCarouselLaptop(item); setCarouselOpen(true); }}>
                    <CardMedia
                      component="img"
                      height="220"
                      image={item.image || ''}
                      alt={item.name}
                      sx={{ objectFit: 'cover', bgcolor: 'grey.200' }}
                    />
                    {item.condition && (
                      <Chip
                        label={filterLabels[item.condition] || item.condition}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          fontWeight: 600,
                          textTransform: 'capitalize',
                        }}
                      />
                    )}
                  </Box>
                  <CardContent sx={{ flex: 1, p: 2.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {item.brand}
                    </Typography>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 1 }}>
                      {item.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
                      <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                        {item.price}
                      </Typography>
                      {item.originalPrice && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textDecoration: 'line-through' }}
                        >
                          {item.originalPrice}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {item.cpu && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CpuIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {item.cpu}
                          </Typography>
                        </Box>
                      )}
                      {item.ram && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StorageIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {item.ram}
                          </Typography>
                        </Box>
                      )}
                      {item.storage && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StorageIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {item.storage}
                          </Typography>
                        </Box>
                      )}
                      {item.display && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DisplayIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {item.display}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button variant="contained" fullWidth size="small" onClick={() => sendLaptopDetailsViaWhatsApp(item)} sx={{ textTransform: 'none' }}>
                        {language === 'ar' ? 'اشتري الآن' : 'Buy Now'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {filtered.length === 0 && (
            <Typography align="center" color="text.secondary" sx={{ py: 8 }}>
              {language === 'ar' ? 'لا توجد أجهزة لعرضها. أضف من لوحة التحكم.' : 'No laptops to show. Add from admin panel.'}
            </Typography>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default PremiumLaptops;
